'use client';
import type { ConnectionStatusProps } from 'elowen-plugin-ui-kit';
import { formatRetryCountdown, formatRetryProgress } from 'elowen-plugin-shared/retryPresentation';
import { useTranslation } from '../../lib/i18n';
import { useNow } from '../../lib/useNow';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { StatusDot } from './DetailPrimitives';

function RetryCountdown({ nextAttemptAt }: { nextAttemptAt: number }) {
  const { locale } = useTranslation();
  const now = useNow();
  return (
    <span data-testid="connection-countdown" aria-hidden className="tabular-nums">
      · {formatRetryCountdown(nextAttemptAt - now, locale)}
    </span>
  );
}

/** Presentation only: the supplied owner keeps retry policy, current authority and recovery actions. */
export function ConnectionStatus({ connectionState, retry, label, onRetry, className }: ConnectionStatusProps) {
  const { t, locale } = useTranslation();
  const text = t.connectionStatus;
  const progress = retry ?? connectionState?.retry;
  const fresh = connectionState === undefined || connectionState?.freshness === 'fresh';
  const transportLabels = {
    connected: text.connected, connecting: text.connecting,
    disconnected: text.disconnected, unknown: text.unknown,
  };
  const countdown = fresh && connectionState?.authority.kind === 'local-owner' && progress?.retryOwner === 'shared'
    && progress.mode !== 'request' && progress.phase === 'scheduled' && progress.nextAttemptAt !== null;
  const parts: string[] = [];
  if (label) parts.push(label);
  // Fresh progress already says what the connection is doing, so it replaces the transport words.
  if (fresh && progress) parts.push(formatRetryProgress(progress, locale, { delay: !countdown }));
  else if (connectionState) {
    if (connectionState.freshness !== 'fresh') {
      parts.push(connectionState.freshness === 'stale' ? text.stale : text.freshnessUnknown);
    }
    parts.push(transportLabels[connectionState.transport]);
    // The dot already says ready; only a connection still waiting for its application handshake needs words.
    if (connectionState.transport === 'connected' && connectionState.readiness === 'pending') parts.push(text.pending);
    if (connectionState.target.kind === 'computer' && connectionState.authority.kind === 'server-observation'
      && connectionState.transport === 'disconnected' && !progress) parts.push(text.remoteRetryUnknown);
  } else if (connectionState === null) {
    parts.push(text.unknown);
  }
  const recoverable = progress
    ? progress.phase === 'exhausted' || progress.phase === 'blocked' || progress.phase === 'cancelled'
    : !connectionState || !fresh || connectionState.transport === 'disconnected' || connectionState.transport === 'unknown';
  const tone = fresh && connectionState?.transport === 'connected' && connectionState.readiness === 'ready' && !progress
    ? 'success' : fresh && (progress?.phase === 'scheduled' || progress?.phase === 'attempting') ? 'warning' : 'muted';

  return (
    <span data-slot="connection-status" className={cn('inline-flex min-w-0 max-w-full flex-wrap items-center gap-x-2 gap-y-1 text-meta text-muted-foreground', className)}>
      {/* The countdown continues the announced line in the same flow, outside the live region that must not tick. */}
      <span className="min-w-0 [overflow-wrap:anywhere]">
        <span role="status" aria-live="polite" aria-atomic="true">
          {tone !== 'success' ? <><StatusDot tone={tone} />{' '}</> : null}{parts.join(' · ')}
        </span>
        {countdown && progress.nextAttemptAt !== null ? <>{' '}<RetryCountdown nextAttemptAt={progress.nextAttemptAt} /></> : null}
      </span>
      {onRetry && recoverable ? <Button type="button" variant="ghost" size="sm" onClick={onRetry}>{t.common.retry}</Button> : null}
    </span>
  );
}
